import { ActionType } from '../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, makeApiReducer, ApiReducer } from '../../../core/api/rsaa';
import { Endpoints } from '../../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';
import { AppState } from '../../../core/redux/appState';
import * as R from 'ramda';
import { AuthState } from '../../authState';


const KEY_ACTION = ActionType.NewPasswordRequest;
const ACTIONS    = [
  ActionType.NewPasswordRequest,
  ActionType.NewPasswordResponse,
  ActionType.NewPasswordError,
];


type Payload = string;


/**
 * Dispatch this action to submit and set the current user's self-selected new password
 *
 * @param password - an encrypted representation of the user's self-selected password
 */
export const newPasswordAction = (password: string) => makeApiAction<Payload>({
  method:   'POST',
  endpoint: Endpoints.UpdatePassword,
  headers:  {
    'content-type': 'application/json',
    'accept':       'application/json',
  },
  body:     (state: AppState) => JSON.stringify({ userId: state.auth.currentUser?.userId, password: password }),
  types:    makeRsaaTypes<Payload>(ACTIONS),
  bailout:  state => !!getLoading(state, KEY_ACTION),
});


const reducer: ApiReducer<Payload> = (state, action, errorMsg, isLoading, payload) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg);
  }

  // The update password endpoint returns 200 on failure, but with an informative error message
  if (payload !== 'Success') {
    return addError(state, KEY_ACTION, payload || '');
  }

  const successState = noLoadingNoError(state, KEY_ACTION);

  const updatedAuth = R.mergeDeepRight<AuthState, AuthState>(
    successState.auth,
    {
      passwordReset:              undefined,
      passwordRequirementMessage: undefined,
      currentUser:                {
        requirePasswordChange: false,
        passwordDigest: 'updated',
      },
    } as AuthState,
  );

  return R.assoc('auth', updatedAuth, successState);
};


export const newPasswordReducer = makeApiReducer<Payload>(ACTIONS, KEY_ACTION, reducer);
